import { DefaultOptionType } from 'antd/lib/select';
import { SorterResult } from 'antd/lib/table/interface';

import { Status } from 'components';

import { mapEnumToOptionsArray } from 'utils';

import { SnapDashboardDto, SnapState } from 'codegen';
import { TColumn, TFieldProps } from 'types';

import { SnapIconStatuses } from '../../Snap/components';

export const fields = (isAdmin: boolean, retailers?: DefaultOptionType[]): TFieldProps[] => [{
  type: TFieldProps.type.DateRangePicker,
  name: 'submittedDates',
  colProps: { xs: 8, xxl: 4 },
  placeholder: 'Submitted Dates',
  itemClassName: 'mb-0',
}, {
  type: TFieldProps.type.Select,
  name: 'merchant',
  options: retailers,
  colProps: { xs: 8, xxl: 4 },
  fieldNames: { value: 'label' },
  placeholder: 'Retailer',
  itemClassName: 'mb-0',
}, {
  type: TFieldProps.type.Select,
  name: 'states',
  mode: 'multiple',
  options: mapEnumToOptionsArray(SnapState),
  colProps: { xs: 8, xxl: 4 },
  placeholder: 'Snap state',
  itemClassName: 'mb-0',
  tagRender: ({ value }) => <Status status={value} />,
  maxTagCount: 'responsive',
}, {
  type: TFieldProps.type.Text,
  name: 'claimReferenceCode',
  colProps: { xs: 4 },
  placeholder: 'Claim ID',
  itemClassName: 'mb-0',
}, {
  type: TFieldProps.type.Text,
  name: 'snapReferenceCode',
  colProps: { xs: 4 },
  placeholder: 'Reference Code',
  itemClassName: 'mb-0',
}, ...(isAdmin
  ? [{
    type: TFieldProps.type.Text,
    name: 'collectorNumber',
    colProps: { xs: 4 },
    placeholder: 'Collector Number',
    itemClassName: 'mb-0',
  }]
  : []
), {
  type: TFieldProps.type.Text,
  name: 'collectorId',
  colProps: { xs: 4 },
  placeholder: 'Collector ID',
  itemClassName: 'mb-0',
}, {
  type: TFieldProps.type.Select,
  name: 'requiresTroubleshooting',
  colProps: { xs: 4 },
  itemClassName: 'mb-0',
  placeholder: 'Claims',
  options: [{
    label: 'Flagged',
    value: 'flagged',
  }, {
    label: 'Unflagged',
    value: 'unflagged',
  }],
}, {
  type: TFieldProps.type.Select,
  name: 'locale',
  colProps: { xs: 4 },
  itemClassName: 'mb-0',
  placeholder: 'Locale',
  options: [{
    label: 'EN',
    value: 'en_CA',
  }, {
    label: 'FR',
    value: 'fr_CA',
  }],
}, {
  type: TFieldProps.type.Select,
  name: 'isPartner',
  colProps: { xs: 4 },
  itemClassName: 'mb-0',
  placeholder: 'Is Partner',
  options: [{
    label: 'Yes',
    value: 'true',
  }, {
    label: 'No',
    value: 'false',
  }],
}, {
  type: TFieldProps.type.Checkbox,
  name: 'isEscalated',
  colProps: { xs: 3 },
  label: 'Escalated',
  itemClassName: 'mb-0',
}, {
  type: TFieldProps.type.Checkbox,
  name: 'isFraudSuspicious',
  colProps: { xs: 3 },
  label: 'Fraud suspicious',
  itemClassName: 'mb-0',
}, {
  type: TFieldProps.type.Checkbox,
  name: 'isCollectorFraudster',
  colProps: { xs: 3 },
  label: 'Fraud collector',
  itemClassName: 'mb-0',
}];

export const columns = (sortOrderInfo?: SorterResult<SnapDashboardDto>): TColumn<SnapDashboardDto>[] => [{
  width: 80,
  title: 'Info',
  dataIndex: 'info',
  render: (_, {
    claimReferenceCode,
    escalateDetails,
    claimDetails,
    fraudSuspicionReason,
    isEscalated,
    requiresTroubleshooting,
    processingByOpsUserId,
    isCollectorFraudster,
    isFraudSuspicious,
  }) => {
    const snapBooleanStatuses = {
      isEscalated,
      isCollectorFraudster,
      isFraudSuspicious,
      isProcessing: !!processingByOpsUserId,
      isRequiresTroubleshooting: requiresTroubleshooting,
    };

    return (
      <SnapIconStatuses
        isSmall
        claimDetails={claimDetails}
        escalateDetails={escalateDetails}
        claimReferenceCode={claimReferenceCode}
        fraudSuspicionReason={fraudSuspicionReason}
        snapBooleanStatuses={snapBooleanStatuses}
      />
    );
  },
}, {
  type: TColumn.type.IDRedirect,
  width: 100,
  title: 'ID',
  dataIndex: 'id',
  sorter: true,
  sortOrderInfo,
}, {
  width: 220,
  title: 'Retailer',
  dataIndex: 'merchant',
}, {
  width: 100,
  title: 'Is Partner',
  align: 'center',
  dataIndex: 'isPartner',
  render: (isPartner) => (isPartner ? 'Y' : 'N'),
}, {
  type: TColumn.type.Currency,
  width: 110,
  title: 'Total Spent',
  dataIndex: 'totalSpent',
  sorter: true,
  sortOrderInfo,
}, {
  type: TColumn.type.Date,
  width: 180,
  title: 'Receipt Date',
  dataIndex: 'receiptTimestamp',
  sorter: true,
  sortOrderInfo,
}, {
  type: TColumn.type.Date,
  width: 180,
  title: 'Submitted From',
  dataIndex: 'submitTimestamp',
  sorter: true,
  sortOrderInfo,
}, {
  width: 110,
  title: 'Miles Earned',
  dataIndex: 'milesEarned',
}, {
  width: 70,
  title: 'Locale',
  dataIndex: 'locale',
  render: (locale) => (locale === 'fr_CA' ? 'FR' : 'EN'),
}, {
  width: 150,
  title: 'Reference Code',
  dataIndex: 'referenceCode',
}, {
  type: TColumn.type.Status,
  width: 200,
  title: 'State',
  dataIndex: 'state',
}];

export const getTroubleshooting = (trouble: string) => {
  if (trouble === 'true') {
    return 'flagged';
  }

  if (trouble === 'false') {
    return 'unflagged';
  }

  return null;
};
